import { DESKTOP_GAME_SOURCE_CONTRACT } from "./sourceContract";

const geometry = DESKTOP_GAME_SOURCE_CONTRACT.tunnelGeometry;
const artboard = DESKTOP_GAME_SOURCE_CONTRACT.artboard;
const FOCAL_LENGTH = (artboard.height / 2)
  / Math.tan((geometry.camera.fov * Math.PI / 180) / 2);

export interface TunnelProjectedRing {
  slot: number;
  sourceIndex: number;
  z: number;
  depth: number;
  x: number;
  y: number;
  width: number;
  height: number;
  opacity: number;
}

export interface TunnelProjectedRect {
  z: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface TunnelProjectionSnapshot {
  worldOffsetModulo: number;
  rings: readonly TunnelProjectedRing[];
  longitudinal: string;
  endVoid: TunnelProjectedRect;
}

export interface TunnelFogStop {
  offset: number;
  opacity: number;
  depth: number;
}

/**
 * Пояс глубины: прямоугольная рамка между двумя дальностями. Из таких поясов
 * собирается маска затухания — она повторяет форму туннеля, а не окружность.
 */
export interface TunnelDepthBand {
  index: number;
  depth: number;
  opacity: number;
  path: string;
}

export interface SerializedTunnelProjection {
  rings: string;
  longitudinal: string;
  endVoid: TunnelProjectedRect;
}

const finite = (value: number): number => Number.isFinite(value) ? value : 0;
const rounded = (value: number, digits = 4): number => Number(value.toFixed(digits));

export function wrapWorldOffset(value: number): number {
  const safe = finite(value);
  const wrapped = safe % geometry.cell;
  if (Math.abs(wrapped) < 1e-9) return 0;
  return rounded(wrapped, 8);
}

export function fogVisibility(depth: number, density: number): number {
  const safeDepth = Math.max(0, finite(depth));
  const safeDensity = Math.max(0, finite(density));
  return Math.exp(-((safeDensity * safeDepth) ** 2));
}

function projectRect(halfWidth: number, halfHeight: number, z: number): TunnelProjectedRect {
  const depth = Math.max(geometry.camera.near, geometry.camera.z - z);
  const projectedHalfWidth = halfWidth * FOCAL_LENGTH / depth;
  const projectedHalfHeight = halfHeight * FOCAL_LENGTH / depth;
  return {
    z: rounded(z, 8),
    x: rounded(artboard.width / 2 - projectedHalfWidth),
    y: rounded(artboard.height / 2 - projectedHalfHeight),
    width: rounded(projectedHalfWidth * 2),
    height: rounded(projectedHalfHeight * 2),
  };
}

const point = (x: number, y: number, z: number): readonly [number, number] => {
  const depth = Math.max(geometry.camera.near, geometry.camera.z - z);
  return [
    rounded(artboard.width / 2 + x * FOCAL_LENGTH / depth),
    rounded(artboard.height / 2 - y * FOCAL_LENGTH / depth),
  ];
};

const pathLine = (x: number, y: number, nearZ: number, farZ: number): string => {
  const [x1, y1] = point(x, y, nearZ);
  const [x2, y2] = point(x, y, farZ);
  return `M${x1} ${y1}L${x2} ${y2}`;
};

function longitudinalPath(rings: readonly TunnelProjectedRing[]): string {
  const nearZ = rings[0]?.z ?? geometry.camera.z - geometry.camera.near;
  const farZ = rings.at(-1)?.z ?? geometry.camera.z - geometry.camera.far;
  const commands: string[] = [];
  for (let index = 0; index < geometry.xGridCount; index += 1) {
    const x = -geometry.halfWidth + index * geometry.cell;
    commands.push(pathLine(x, -geometry.halfHeight, nearZ, farZ));
    commands.push(pathLine(x, geometry.halfHeight, nearZ, farZ));
  }
  for (let index = 0; index < geometry.yGridCount; index += 1) {
    const y = -geometry.halfHeight + index * geometry.cell;
    commands.push(pathLine(-geometry.halfWidth, y, nearZ, farZ));
    commands.push(pathLine(geometry.halfWidth, y, nearZ, farZ));
  }
  return commands.join("");
}

export function sampleSquareTunnelProjection(worldOffset: number): TunnelProjectionSnapshot {
  const worldOffsetModulo = wrapWorldOffset(worldOffset);
  const rings: TunnelProjectedRing[] = [];
  for (let sourceIndex = 0; sourceIndex < geometry.ringCount; sourceIndex += 1) {
    const z = geometry.zFront - sourceIndex * geometry.cell + worldOffsetModulo;
    const depth = geometry.camera.z - z;
    if (depth <= geometry.camera.near || depth > geometry.camera.far) continue;
    const projected = projectRect(geometry.halfWidth, geometry.halfHeight, z);
    rings.push({
      slot: rings.length,
      sourceIndex,
      z: rounded(z, 8),
      depth: rounded(depth, 8),
      x: projected.x,
      y: projected.y,
      width: projected.width,
      height: projected.height,
      opacity: rounded(geometry.lineOpacity * fogVisibility(depth, geometry.fogDensity), 8),
    });
  }

  const endVoid = projectRect(
    geometry.halfWidth * geometry.endVoidScale,
    geometry.halfHeight * geometry.endVoidScale,
    geometry.endVoidZ + worldOffsetModulo,
  );
  return {
    worldOffsetModulo,
    rings,
    longitudinal: longitudinalPath(rings),
    endVoid,
  };
}

export function serializeTunnelProjection(snapshot: TunnelProjectionSnapshot): SerializedTunnelProjection {
  return {
    rings: snapshot.rings.map((ring) => (
      `M${ring.x} ${ring.y}h${ring.width}v${ring.height}h-${ring.width}Z`
    )).join(""),
    longitudinal: snapshot.longitudinal,
    endVoid: { ...snapshot.endVoid },
  };
}

export function buildTunnelFogStops(): readonly TunnelFogStop[] {
  const diagonalRadius = Math.hypot(artboard.width / 2, artboard.height / 2);
  const depths = [
    geometry.camera.far, 180, 128, 92, 64, 42, 28, 18, 12, 8, 5, 3, 1,
  ];
  const stops = depths.map((depth) => {
    const projectedRadius = Math.hypot(
      geometry.halfWidth * FOCAL_LENGTH / depth,
      geometry.halfHeight * FOCAL_LENGTH / depth,
    );
    return {
      offset: rounded(Math.min(1, projectedRadius / diagonalRadius), 6),
      opacity: rounded(geometry.lineOpacity * fogVisibility(depth, geometry.fogDensity), 8),
      depth,
    };
  }).sort((left, right) => left.offset - right.offset);

  const unique: TunnelFogStop[] = [{
    offset: 0,
    opacity: rounded(geometry.lineOpacity * fogVisibility(geometry.camera.far, geometry.fogDensity), 8),
    depth: geometry.camera.far,
  }];
  for (const stop of stops) {
    if (stop.offset <= 0 || stop.offset >= 1) continue;
    if (unique.at(-1)?.offset === stop.offset) continue;
    unique.push(stop);
  }
  unique.push({ offset: 1, opacity: geometry.lineOpacity, depth: geometry.camera.near });
  return unique;
}


const DEPTH_BAND_COUNT = 40;

const rectPath = (rect: TunnelProjectedRect): string =>
  `M${rect.x} ${rect.y}h${rect.width}v${rect.height}h${-rect.width}Z`;

/**
 * Пояса затухания от ближней плоскости к дальней.
 *
 * Каждый пояс — прямоугольная рамка между двумя дальностями, поэтому пояса не
 * перекрываются и каждый несёт собственную видимость. Дальний край туннеля
 * получается прямоугольным, как вся геометрия приложения; круглой дымки,
 * которая раньше рисовала овальный конец, больше нет.
 */
export function buildTunnelDepthBands(): readonly TunnelDepthBand[] {
  const near = geometry.camera.near;
  const far = geometry.camera.far;
  const ratio = far / near;
  const bands: TunnelDepthBand[] = [];
  let outer = projectRect(geometry.halfWidth, geometry.halfHeight, geometry.camera.z - near);
  for (let index = 0; index < DEPTH_BAND_COUNT; index += 1) {
    const depth = near * ratio ** ((index + 1) / DEPTH_BAND_COUNT);
    const inner = projectRect(geometry.halfWidth, geometry.halfHeight, geometry.camera.z - depth);
    const mid = Math.sqrt(Math.max(near, depth / ratio ** (1 / DEPTH_BAND_COUNT)) * depth);
    bands.push({
      index,
      depth: rounded(depth, 4),
      opacity: rounded(fogVisibility(mid, geometry.fogDensity), 6),
      path: `${rectPath(outer)}${rectPath(inner)}`,
    });
    outer = inner;
  }
  // Сердцевина: всё, что дальше последнего пояса, гаснет полностью.
  bands.push({
    index: DEPTH_BAND_COUNT,
    depth: rounded(far, 4),
    opacity: rounded(fogVisibility(far, geometry.fogDensity), 6),
    path: rectPath(outer),
  });
  return bands;
}
